import { useState } from 'react';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import { fmtMonthShort } from '@v2/pages/commissioning/CommissionExecutiveCharts';
import ForecastMonthDetailModal from '@v2/pages/commissioning/ForecastMonthDetailModal';
import type {
    SellerCommissionForecast,
    SellerCommissionForecastMonth,
} from '@v2/pages/commissioning/types';

function brlCompact(v: number) {
    if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);
}

function brl(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);
}

type Props = {
    forecast: SellerCommissionForecast | null | undefined;
};

/** Faixa compacta de previsão 12 meses para a home do vendedor. */
export default function SellerForecastStrip({ forecast }: Props) {
    const { t } = useI18n();
    const [selectedMonth, setSelectedMonth] = useState<SellerCommissionForecastMonth | null>(null);
    const months = forecast?.months ?? [];
    const horizon = forecast?.horizon_months ?? 12;
    const horizonTotal = forecast?.horizon_total ?? months.reduce((s, m) => s + m.amount, 0);
    const installmentsCount = forecast?.horizon_installments_count ?? forecast?.installments_count ?? 0;

    if (!forecast || months.length === 0) {
        return (
            <section className="overflow-hidden rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center dark:border-zinc-700 dark:bg-zinc-800/40">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                    {t('v2.my_commission.forecast_title')}
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                    {t('v2.my_commission.forecast_empty')}
                </p>
            </section>
        );
    }

    const maxAmount = Math.max(...months.map((m) => m.amount), 0);

    return (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
                <div className="flex flex-wrap items-end justify-between gap-2">
                    <div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                            {t('v2.my_commission.forecast_title')}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-zinc-400">
                            {t('v2.my_commission.forecast_subtitle', { months: String(horizon) })}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-black tabular-nums text-orange-700 dark:text-orange-300">
                            {brl(horizonTotal)}
                        </p>
                        <p className="text-[10px] text-gray-400">
                            {t('v2.my_commission.forecast_total_hint', {
                                count: String(installmentsCount),
                            })}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {months.map((m, i) => {
                    const isCurrent = i === 0;
                    const intensity = maxAmount > 0 ? m.amount / maxAmount : 0;
                    const barPct = Math.max(m.amount > 0 ? 8 : 0, intensity * 100);
                    const clickable = m.amount > 0;

                    return (
                        <button
                            key={m.month}
                            type="button"
                            disabled={!clickable}
                            onClick={() => clickable && setSelectedMonth(m)}
                            className={cn(
                                'flex flex-col overflow-hidden rounded-xl border p-2.5 text-left transition',
                                isCurrent
                                    ? 'border-amber-300 bg-amber-50/90 dark:border-amber-700/60 dark:bg-amber-950/30'
                                    : m.amount > 0
                                        ? 'cursor-pointer border-orange-100 bg-white hover:border-orange-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-orange-700'
                                        : 'cursor-default border-gray-100 bg-gray-50/60 dark:border-zinc-800 dark:bg-zinc-900/40',
                            )}
                        >
                            <div className="mb-1 flex items-start justify-between gap-1">
                                <span className="text-[11px] font-semibold capitalize text-gray-800 dark:text-zinc-100">
                                    {fmtMonthShort(m.month)}
                                </span>
                                {isCurrent && (
                                    <span className="shrink-0 rounded-full bg-amber-200/90 px-1 py-0.5 text-[8px] font-bold uppercase text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                                        {t('v2.my_commission.forecast_now')}
                                    </span>
                                )}
                            </div>
                            <p className={cn(
                                'text-sm font-bold tabular-nums',
                                m.amount > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-gray-300 dark:text-zinc-600',
                            )}>
                                {m.amount > 0 ? brlCompact(m.amount) : '—'}
                            </p>
                            <p className="mt-0.5 text-[9px] text-gray-400">
                                {m.installments_count > 0
                                    ? t('v2.my_commission.forecast_month_count', {
                                        count: String(m.installments_count),
                                    })
                                    : t('v2.my_commission.forecast_month_empty')}
                            </p>
                            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                                <div
                                    className={cn('h-full rounded-full', isCurrent ? 'bg-amber-500' : 'bg-orange-500')}
                                    style={{ width: `${barPct}%` }}
                                />
                            </div>
                        </button>
                    );
                })}
            </div>

            <ForecastMonthDetailModal month={selectedMonth} onClose={() => setSelectedMonth(null)} />
        </section>
    );
}
